import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { obtenerTodasLasCotizaciones } from '../services/cotizaciones/cotizacionesService';
import { generarGuiaEnvia, generarGuiaEnviaCom } from '../services/cotizaciones';
import { CotizacionesState, CotizacionData } from '../types/cotizaciones';
import { enrichFormDataForQuote } from '../utils/quoteFormDataUtils';
import { quoteExceedsShippingLimit } from '../utils/shippingQuoteLimit';
// Validación simple de datos del formulario
const validateFormData = (formData: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!formData) {
    errors.push('Datos del formulario faltantes');
    return { isValid: false, errors };
  }

  // Validar campos numéricos requeridos
  const camposNumericos = [
    { campo: 'peso', nombre: 'Peso', min: 0.1 },
    { campo: 'largo', nombre: 'Largo', min: 1 },
    { campo: 'ancho', nombre: 'Ancho', min: 1 },
    { campo: 'altura', nombre: 'Altura', min: 1 }
  ];

  camposNumericos.forEach(({ campo, nombre, min }) => {
    const valor = formData[campo];
    if (!valor || isNaN(valor) || valor < min) {
      errors.push(`${nombre} debe ser mayor a ${min}`);
    }
  });

  if (!String(formData.destinoCiudad || '').trim()) {
    errors.push('Seleccione la ciudad de destino');
  }
  if (!String(formData.destinoDireccion || '').trim()) {
    errors.push('Ingrese la dirección de destino');
  }

  return { isValid: errors.length === 0, errors };
};


export const useCotizaciones = () => {
  const [cotizaciones, setCotizaciones] = useState<CotizacionesState>({
    envia: null,
    noventa9Minutos: null,
    interRapidisimo: null,
    deprisa: null,
    coordinadora: null,
    loading: false,
    error: null
  });

  const [showModal, setShowModal] = useState(false);
  const [todasLasCotizaciones, setTodasLasCotizaciones] = useState<CotizacionData[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedCotizacion, setSelectedCotizacion] = useState<CotizacionData | null>(null);
  const [showGuiaModal, setShowGuiaModal] = useState(false);
  const [guiaData, setGuiaData] = useState<any>(null);
  const [generatingGuia, setGeneratingGuia] = useState(false);

  const cotizar = useCallback(async (formData: any, orderDetail: any, selectedTipoServicio: string) => {
    try {
      // Validar datos del formulario
      const validation = validateFormData(formData);
      if (!validation.isValid) {
        throw new Error(`Datos del formulario inválidos: ${validation.errors.join(', ')}`);
      }

      // Validar que el peso sea mínimo 1kg
      if (formData.peso < 1) {
        formData.peso = 1;
        console.warn('⚠️ Peso ajustado a 1kg (mínimo requerido)');
      }

      // Abrir modal inmediatamente y mostrar loading
      setShowModal(true);
      setCotizaciones(prev => ({ ...prev, loading: true, error: null }));
      setTodasLasCotizaciones([]);
      
      // Log para debug del área metropolitana
      console.log('🏙️ Ciudad destino:', orderDetail?.shippingAddress?.city);
      console.log('🏙️ Datos completos del pedido:', orderDetail);
      console.log('📋 Datos del formulario validados:', formData);
      
      const resultado = await obtenerTodasLasCotizaciones(formData, orderDetail, selectedTipoServicio);
      
      console.log('🔍 Resultado de cotizaciones:', resultado);
      
      // Procesar y combinar todas las cotizaciones
      const cotizacionesCombinadas: CotizacionData[] = [];
      
      // Agregar cotización de Envía
      if (resultado.envia?.success) {
        console.log('✅ Envía: Cotización exitosa');
        const precioEnvia = Math.round(resultado.envia.data.valor_flete + 
                           resultado.envia.data.valor_costom + 
                           resultado.envia.data.valor_otros);
        
        cotizacionesCombinadas.push({
          carrier: 'Envía',
          success: true,
          data: resultado.envia.data,
          precio: precioEnvia,
          diasEntrega: resultado.envia.data.dias_entrega
        });
      } else {
        console.log('❌ Envía: No hay cotización disponible');
      }
      
      // Agregar cotizaciones de 99 Minutos (solo si se solicitó)
      if (resultado.noventa9Minutos?.quote && 
          resultado.noventa9Minutos.quote.success && 
          resultado.noventa9Minutos.quote.data?.data &&
          resultado.noventa9Minutos.quote.data.meta !== 'error') {
        
        console.log('✅ 99 Minutos: Cotización exitosa');
        console.log('🔍 Servicios disponibles de 99 Minutos:', resultado.noventa9Minutos.quote.data.data.map((s: any) => s.service));
        
        resultado.noventa9Minutos.quote.data.data.forEach((service: any) => {
          // Filtrar cotizaciones con service "carga_do" y "mensajeria_do" (igual que en cotizacionesService.ts)
          if (service.service !== 'carga_do' && service.service !== 'mensajeria_do') {
            console.log('✅ Agregando servicio de 99 Minutos:', service.service, 'Precio:', Math.round(service.totalPrice));
            cotizacionesCombinadas.push({
              carrier: 'Noventa9Minutos',
              success: true,
              data: {
                ...service,
                carrierName: 'Noventa9Minutos',
                serviceDescription: service.serviceDescription,
                deliveryEstimate: service.deliveryEstimate,
                deliveryDate: service.deliveryDate
              },
              precio: Math.round(service.totalPrice || service.price || 0),
              diasEntrega: service.deliveryDate?.dateDifference || service.dias_entrega || service.delivery_days || 0
            });
          } else {
            console.log('🚫 Filtrando servicio de 99 Minutos:', service.service);
          }
        });
      } else if (resultado.noventa9Minutos === null) {
        console.log('🚫 99 Minutos: No se solicitó cotización (fuera del área metropolitana)');
      } else {
        console.log('❌ 99 Minutos: No hay cotización disponible o hay error:', resultado.noventa9Minutos?.quote?.data?.error);
      }

      // Agregar cotizaciones de Inter Rapidisimo
      if (resultado.interRapidisimo?.quote && 
          resultado.interRapidisimo.quote.success && 
          resultado.interRapidisimo.quote.data?.data &&
          resultado.interRapidisimo.quote.data.meta !== 'error') {
        
        console.log('✅ InterRapidisimo: Cotización exitosa');
        console.log('🔍 Servicios disponibles de InterRapidisimo:', resultado.interRapidisimo.quote.data.data.map((s: any) => s.service));
        
        resultado.interRapidisimo.quote.data.data.forEach((service: any) => {
          // Filtrar cotizaciones con service "carga_do" y "mensajeria_do" (igual que en cotizacionesService.ts)
          if (service.service !== 'carga_do' && service.service !== 'mensajeria_do') {
            console.log('✅ Agregando servicio de InterRapidisimo:', service.service, 'Precio:', Math.round(service.totalPrice));
            cotizacionesCombinadas.push({
              carrier: 'InterRapidisimo',
              success: true,
              data: {
                ...service,
                carrierName: 'InterRapidisimo',
                serviceDescription: service.serviceDescription,
                deliveryEstimate: service.deliveryEstimate,
                deliveryDate: service.deliveryDate
              },
              precio: Math.round(service.totalPrice || service.price || 0),
              diasEntrega: service.deliveryDate?.dateDifference || service.dias_entrega || service.delivery_days || 0
            });
          } else {
            console.log('🚫 Filtrando servicio de InterRapidisimo:', service.service);
          }
        });
      } else {
        console.log('❌ InterRapidisimo: No hay cotización disponible o hay error:', resultado.interRapidisimo?.quote?.data?.error);
      }

      // Agregar cotizaciones de Deprisa
      if (resultado.deprisa?.quote && 
          resultado.deprisa.quote.success && 
          resultado.deprisa.quote.data?.data &&
          resultado.deprisa.quote.data.meta !== 'error') {
        
        console.log('✅ Deprisa: Cotización exitosa');
        console.log('🔍 Servicios disponibles de Deprisa:', resultado.deprisa.quote.data.data.map((s: any) => s.service));
        
        resultado.deprisa.quote.data.data.forEach((service: any) => {
          // Filtrar cotizaciones con service "carga_do" y "mensajeria_do" (igual que en cotizacionesService.ts)
          if (service.service !== 'carga_do' && service.service !== 'mensajeria_do') {
            console.log('✅ Agregando servicio de Deprisa:', service.service, 'Precio:', Math.round(service.totalPrice));
            cotizacionesCombinadas.push({
              carrier: 'Deprisa',
              success: true,
              data: {
                ...service,
                carrierName: 'Deprisa',
                serviceDescription: 'Deprisa Estandar',
                deliveryEstimate: service.deliveryEstimate,
                deliveryDate: service.deliveryDate
              },
              precio: Math.round(service.totalPrice || service.price || 0),
              diasEntrega: service.deliveryDate?.dateDifference || service.dias_entrega || service.delivery_days || 0
            });
          } else {
            console.log('🚫 Filtrando servicio de Deprisa:', service.service);
          }
        });
      } else {
        console.log('❌ Deprisa: No hay cotización disponible o hay error:', resultado.deprisa?.quote?.data?.error);
      }

      // Agregar cotizaciones de Coordinadora
      if (resultado.coordinadora?.quote &&
          resultado.coordinadora.quote.success &&
          resultado.coordinadora.quote.data?.data &&
          resultado.coordinadora.quote.data.meta !== 'error') {

        console.log('✅ Coordinadora: Cotización exitosa');
        console.log('🔍 Servicios disponibles de Coordinadora:', resultado.coordinadora.quote.data.data.map((s: any) => s.service));

        resultado.coordinadora.quote.data.data.forEach((service: any) => {
          if (service.service !== 'carga_do' && service.service !== 'mensajeria_do') {
            console.log('✅ Agregando servicio de Coordinadora:', service.service, 'Precio:', Math.round(service.totalPrice));
            cotizacionesCombinadas.push({
              carrier: 'Coordinadora',
              success: true,
              data: {
                ...service,
                carrierName: 'Coordinadora',
                serviceDescription: 'Coordinadora Ecommerce',
                deliveryEstimate: service.deliveryEstimate,
                deliveryDate: service.deliveryDate
              },
              precio: Math.round(service.totalPrice || service.price || 0),
              diasEntrega: service.deliveryDate?.dateDifference || service.dias_entrega || service.delivery_days || 0
            });
          } else {
            console.log('🚫 Filtrando servicio de Coordinadora:', service.service);
          }
        });
      } else {
        console.log('❌ Coordinadora: No hay cotización disponible o hay error:', resultado.coordinadora?.quote?.data?.error);
      }

      // Ordenar por precio (más barato primero)
      cotizacionesCombinadas.sort((a, b) => a.precio - b.precio);
      
      console.log('📊 Cotizaciones combinadas finales:', cotizacionesCombinadas);
      
      setTodasLasCotizaciones(cotizacionesCombinadas);
      
      // SIEMPRE mostrar cotizaciones exitosas, sin importar si algunas transportadoras fallaron
      if (cotizacionesCombinadas.length > 0) {
        // Actualizar estado SIN error porque hay cotizaciones exitosas
        setCotizaciones(prev => ({
          ...resultado,
          loading: false,
          error: null // No hay error si hay cotizaciones disponibles
        }));
        
        toast.success(`Se encontraron ${cotizacionesCombinadas.length} cotizaciones disponibles`, { icon: "✅" });
        
        // Mostrar información adicional sobre qué transportadoras están disponibles
        const carriers = Array.from(new Set(cotizacionesCombinadas.map(c => c.carrier)));
        console.log(`🚚 Transportadoras disponibles: ${carriers.join(', ')}`);
        
        // Mostrar información sobre transportadoras no disponibles (solo como info, no como error)
        const carriersNoDisponibles = [];
        if (resultado.envia && !resultado.envia.success) {
          carriersNoDisponibles.push(`Envía: ${resultado.envia.data?.respuesta || 'Sin cobertura'}`);
        }
        if (resultado.noventa9Minutos && !resultado.noventa9Minutos.success) {
          carriersNoDisponibles.push(`99 Minutos: Sin cotizaciones`);
        }
        if (resultado.interRapidisimo && !resultado.interRapidisimo.success) {
          carriersNoDisponibles.push(`InterRapidisimo: Sin cotizaciones`);
        }
        if (resultado.deprisa && !resultado.deprisa.success) {
          carriersNoDisponibles.push(`Deprisa: Sin cotizaciones`);
        }
        if (resultado.coordinadora && !resultado.coordinadora.success) {
          carriersNoDisponibles.push(`Coordinadora: Sin cotizaciones`);
        }

        if (carriersNoDisponibles.length > 0) {
          console.log('ℹ️ Transportadoras no disponibles:', carriersNoDisponibles.join(', '));
        }
      } else {
        // Solo mostrar error si NO hay NINGUNA cotización exitosa
        setCotizaciones(prev => ({
          ...resultado,
          loading: false,
          error: resultado.error || 'No se pudieron obtener cotizaciones de ninguna transportadora'
        }));
        toast.error("No se encontraron cotizaciones disponibles", { icon: "⚠️" });
      }
      
    } catch (error) {
      console.error('❌ Error crítico al cotizar:', error);
      
      // Determinar el tipo de error y mostrar mensaje específico
      let errorMessage = 'Error al obtener cotizaciones';
      if (error instanceof Error) {
        if (error.message.includes('Problema de conectividad')) {
          errorMessage = 'Error de conexión con el servidor';
        } else if (error.message.includes('Datos del formulario inválidos')) {
          errorMessage = 'Por favor revisa los datos del formulario';
        } else {
          errorMessage = error.message;
        }
      }
      
      setCotizaciones(prev => ({ 
        ...prev, 
        error: errorMessage,
        loading: false 
      }));
      toast.error(errorMessage, { icon: "⚠️" });
    }
  }, []);

  const cerrarModal = useCallback(() => {
    setShowModal(false);
    setTodasLasCotizaciones([]);
  }, []);

  const seleccionarCotizacion = useCallback((cotizacion: CotizacionData, maxQuotePrice?: number | null) => {
    if (quoteExceedsShippingLimit(cotizacion.precio, maxQuotePrice ?? null)) {
      toast.error('Esta cotización supera el costo de envío del pedido', { icon: '⚠️' });
      return;
    }
    setSelectedCotizacion(cotizacion);
    setShowConfirmationModal(true);
  }, []);

  const confirmarCotizacion = useCallback(async (formData: any, orderDetail: any, selectedTipoServicio: string) => {
    if (!selectedCotizacion) return;

    const preparedFormData = enrichFormDataForQuote(formData, orderDetail)
    
    setShowConfirmationModal(false);
    
    if (selectedCotizacion.carrier === 'Envía') {
      // Para Envía, proceder con la generación de guía
      try {
        setGeneratingGuia(true);
        toast.success(`Generando guía con ${selectedCotizacion.carrier}`, { icon: "✅" });
        
        // Llamar a la función para generar la guía
        const response = await generarGuiaEnvia(selectedCotizacion.data, preparedFormData, orderDetail, selectedTipoServicio);
        
        if (response.success && response.data) {
          // Mostrar el modal con la información de la guía
          setGuiaData(response.data);
          setShowGuiaModal(true);
          
          // Cerrar el modal de cotizaciones
          setShowModal(false);
        } else {
          throw new Error('Error al generar la guía');
        }
        
      } catch (error) {
        console.error('Error al generar guía:', error);
        toast.error('Error al generar la guía', { icon: "⚠️" });
      } finally {
        setGeneratingGuia(false);
      }
    } else {
      // Para InterRapidisimo, Noventa9Minutos y Deprisa, usar el nuevo endpoint
      try {
        setGeneratingGuia(true);
        toast.success(`Generando guía con ${selectedCotizacion.carrier}`, { icon: "✅" });
        
        // Llamar a la función para generar la guía con Envia.com
        const response = await generarGuiaEnviaCom(selectedCotizacion, preparedFormData, orderDetail);
        
        if (response.success && response.data) {
          // Verificar si hay un error en la respuesta
          if (response.data.meta === 'error') {
            // Mostrar el mensaje de error específico
            const errorMessage = response.data.error?.message || response.data.error?.description || 'Error desconocido';
            toast.error(`${selectedCotizacion.carrier}: ${errorMessage}`, { 
              icon: "⚠️",
              duration: 6000 // Mostrar por más tiempo para errores importantes
            });
            return;
          }
          
          // Procesar la respuesta exitosa de Envia.com
          const guiaData = {
            guia: String(response.data.data[0].trackingNumber || ''),
            valor_flete: response.data.data[0].totalPrice,
            dias_entrega: selectedCotizacion.diasEntrega || 0,
            urlguia: response.data.data[0].label,
            trackUrl: response.data.data[0].trackUrl,
            shipmentId: response.data.data[0].shipmentId,
            currency: response.data.data[0].currency,
            currentBalance: response.data.data[0].currentBalance,
            respuesta: `Guía generada exitosamente con ${selectedCotizacion.carrier}`,
            carrier: selectedCotizacion.carrier,
            service: selectedCotizacion.data?.serviceDescription || 'Estándar',
            orderUpdated: response.orderUpdated || false,
            orderId: response.orderId || orderDetail.id.toString(),
            updateResponse: {
              message: response.message || 'La guía ha sido asignada al pedido'
            }
          };
          
          // Mostrar el modal con la información de la guía
          setGuiaData(guiaData);
          setShowGuiaModal(true);
          
          // Cerrar el modal de cotizaciones
          setShowModal(false);
        } else {
          throw new Error(response.message || 'Error al generar la guía');
        }
        
      } catch (error) {
        console.error('Error al generar guía:', error);
        toast.error(`Error al generar la guía con ${selectedCotizacion.carrier}`, { icon: "⚠️" });
      } finally {
        setGeneratingGuia(false);
      }
    }
    
    setSelectedCotizacion(null);
  }, [selectedCotizacion]);

  const cancelarCotizacion = useCallback(() => {
    setShowConfirmationModal(false);
    setSelectedCotizacion(null);
  }, []);

  const handleCloseGuiaModal = useCallback(() => {
    setShowGuiaModal(false);
    setGuiaData(null);
    // Recargar la página para mostrar los cambios
    window.location.reload();
  }, []);

  return {
    cotizaciones,
    todasLasCotizaciones,
    showModal,
    setShowModal,
    cotizar,
    cerrarModal,
    seleccionarCotizacion,
    showConfirmationModal,
    selectedCotizacion,
    confirmarCotizacion,
    cancelarCotizacion,
    showGuiaModal,
    guiaData,
    handleCloseGuiaModal,
    generatingGuia
  };
};
